import { createTheme, type Theme } from "@mui/material/styles";

export type ThemeMode = "light" | "dark";

const lightTokens = {
  primaryMain: "#0d7377",
  primaryDark: "#0b5d60",
  primaryLight: "#14a1a7",
  secondaryMain: "#84f24a",
  secondaryDark: "#6eea2f",
  backgroundDefault: "#ffffff",
  backgroundPaper: "#ffffff",
  backgroundMuted: "#f3f6f7",
  surfaceSubtle: "#f8fafc",
  surfaceHover: "#eef6f6",
  textPrimary: "#111827",
  textSecondary: "#6b7280",
  divider: "#e5e7eb",
  borderSubtle: "#dbe3e6",
  borderStrong: "#cbd5e1",
}

const darkTokens = {
  primaryMain: "#3dd184",
  primaryDark: "#2aa865",
  primaryLight: "#66dc9b",
  secondaryMain: "#84f24a",
  secondaryDark: "#6eea2f",
  backgroundDefault: "#0a1214",
  backgroundPaper: "#121b1e",
  backgroundMuted: "#0f181b",
  surfaceSubtle: "#18242a",
  surfaceHover: "#1d2e33",
  textPrimary: "#f1f5f9",
  textSecondary: "#94a3b8",
  divider: "#1f2d31",
  borderSubtle: "#22323a",
  borderStrong: "#2e4047",
}

export function createAppTheme(mode: ThemeMode): Theme {
  const t = mode === "dark" ? darkTokens : lightTokens

  return createTheme({
    palette: {
      mode,
      primary: {
        main: t.primaryMain,
        dark: t.primaryDark,
        light: t.primaryLight,
        contrastText: "#ffffff",
      },
      secondary: {
        main: t.secondaryMain,
        dark: t.secondaryDark,
        contrastText: "#111827",
      },
      background: {
        default: t.backgroundDefault,
        paper: t.backgroundPaper,
      },
      text: {
        primary: t.textPrimary,
        secondary: t.textSecondary,
      },
      divider: t.divider,
    },
    shape: {
      borderRadius: 8,
    },
    spacing: 8,
    typography: {
      fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
      button: {
        textTransform: "none",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            scrollBehavior: "smooth",
          },
          body: {
            backgroundColor: t.backgroundDefault,
            color: t.textPrimary,
          },
          "#root": {
            minHeight: "100vh",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 600,
          },
        },
      },
      MuiContainer: {
        defaultProps: {
          maxWidth: "lg",
        },
        styleOverrides: {
          maxWidthLg: {
            "@media (min-width:1200px)": {
              maxWidth: "1320px",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            "& fieldset": {
              borderColor: t.borderSubtle,
            },
            "&:hover fieldset": {
              borderColor: t.primaryMain,
            },
            "&.Mui-focused fieldset": {
              borderColor: t.primaryMain,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: t.divider,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 12,
          },
        },
      },
    },
  })
}

export const appTheme = createAppTheme("light")
